import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * 表单缓存项
 */
interface FormCacheItem {
  /** 表单数据 */
  data: Record<string, unknown>
  /** 缓存时间戳 */
  timestamp: number
  /** 是否为脏数据（已修改未保存） */
  isDirty: boolean
}

/**
 * 表单缓存 Store 状态
 */
interface FormCacheState {
  /** 缓存数据，key 为页面路径 */
  cache: Record<string, FormCacheItem>

  /** 设置表单缓存 */
  setFormCache: (path: string, data: Record<string, unknown>, isDirty?: boolean) => void

  /** 获取表单缓存 */
  getFormCache: (path: string) => FormCacheItem | null

  /** 清除指定路径的缓存 */
  clearFormCache: (path: string) => void

  /** 清除多个路径的缓存 */
  clearFormCaches: (paths: string[]) => void

  /** 标记为干净（已保存） */
  markAsClean: (path: string) => void

  /** 清除所有缓存 */
  clearAll: () => void
}

/**
 * 表单缓存 Store
 * 用于在标签页切换时保持表单状态
 */
export const useFormCacheStore = create<FormCacheState>()(
  persist(
    (set, get) => ({
      cache: {},

      setFormCache: (path, data, isDirty = true) =>
        set((state) => ({
          cache: {
            ...state.cache,
            [path]: {
              data,
              timestamp: Date.now(),
              isDirty,
            },
          },
        })),

      getFormCache: (path) => {
        const state = get()
        return state.cache[path] || null
      },

      clearFormCache: (path) =>
        set((state) => {
          const { [path]: _, ...rest } = state.cache
          return { cache: rest }
        }),

      clearFormCaches: (paths) =>
        set((state) => {
          const newCache = { ...state.cache }
          for (const path of paths) {
            delete newCache[path]
          }
          return { cache: newCache }
        }),

      markAsClean: (path) =>
        set((state) => {
          const item = state.cache[path]
          if (!item) return state
          return {
            cache: {
              ...state.cache,
              [path]: { ...item, isDirty: false },
            },
          }
        }),

      clearAll: () => set({ cache: {} }),
    }),
    {
      name: 'form-cache-storage',
      // 只持久化 cache 数据
      partialize: (state) => ({ cache: state.cache }),
    }
  )
)
